import {
  IconButton as MuiIconButton,
  type IconButtonProps as MuiIconButtonProps,
  Tooltip as MuiTooltip,
  type TooltipProps as MuiTooltipProps,
} from '@mui/material';

import { useTheme } from '../../utils/theme';
import type { BaseComponentProps } from '../../utils/types';
import { Icon, type IconChildComponentProps } from '../icon/Icon';

export interface IconButtonProps
  extends BaseComponentProps,
    IconChildComponentProps,
    Pick<
      MuiIconButtonProps,
      | 'color'
      | 'onClick'
      | 'disabled'
      | 'edge'
      | 'size'
      | 'type'
      | 'disableRipple'
    > {
  tooltip: MuiTooltipProps['title'];
  tooltipPlacement?: MuiTooltipProps['placement'];
  variant?: 'filled' | 'outlined';
  iconOnly?: boolean; // Remove padding around icon
  withBorder?: boolean;
}

export const IconButton = ({
  icon,
  iconProps,
  tooltip,
  iconOnly,
  tooltipPlacement,
  withBorder,
  color,
  ...buttonProps
}: IconButtonProps) => {
  const theme = useTheme();
  // We cannot use the theme component default prop color in themes/core/component/MuiIconButton
  // as it also affects other components using IconButton in their structure.
  // Making the theme identifiable by name and using it to set the default color is a workaround.
  const defaultColor = theme.name === 'core' ? 'secondary' : undefined;
  return (
    <MuiTooltip title={tooltip} placement={tooltipPlacement}>
      <MuiIconButton
        aria-label={String(tooltip)}
        color={color || defaultColor}
        {...buttonProps}
        sx={{
          border: withBorder ? 1 : 0,
          padding: iconOnly ? 0 : undefined,
        }}
      >
        <Icon {...iconProps}>{icon}</Icon>
      </MuiIconButton>
    </MuiTooltip>
  );
};
